const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

export class ApiError extends Error {
  code: string
  details?: unknown

  constructor(code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.details = details
  }
}

interface ApiSuccessResponse<T> {
  success: true
  data: T
}

interface ApiErrorResponse {
  success: false
  error: { code: string; message: string; details?: unknown }
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiSuccessResponse<T>> {
  const sessionId = localStorage.getItem('unitexrpl_session')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers })
  } catch {
    throw new ApiError('NETWORK_ERROR', 'Unable to reach the server. Check your connection.')
  }

  let body: ApiResponse<T>
  try {
    body = await response.json()
  } catch {
    throw new ApiError('PARSE_ERROR', 'Received an invalid response from the server.')
  }

  if (!response.ok || !body.success) {
    const err = 'error' in body ? body.error : undefined
    throw new ApiError(
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed with status ${response.status}`,
      err?.details,
    )
  }

  return body
}

// ── Xaman sign-in payload types ──

export interface SignInPayload {
  uuid: string
  qrUrl: string
  deepLink: string
  websocketUrl: string
}

export type SignInStatus =
  | { status: 'pending' }
  | { status: 'expired' }
  | { status: 'rejected' }
  | { status: 'signed'; sessionId: string; walletAddress: string }

export interface SessionData {
  walletAddress: string
  publicKey: string
  createdAt: number
  expiresAt: number
}

export interface UserData {
  id: number
  walletAddress: string
  publicKey: string
  createdAt: number
  updatedAt: number
}

export const api = {
  createSignIn: (returnUrl?: string) =>
    request<SignInPayload>('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(returnUrl ? { returnUrl } : {}),
    }),

  checkSignIn: (uuid: string) =>
    request<SignInStatus>(`/auth/signin/${uuid}`),

  getSession: () =>
    request<{ session: SessionData }>('/auth/session'),

  logout: () =>
    request<null>('/auth/logout', { method: 'POST' }),

  getUser: () =>
    request<UserData>('/user/me'),
}
